'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, useRef } from 'react';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import Image from '@/components/Image';
import FormBlock from '@/components/WebsiteProdPage/ScheduleMeeting/FormBlock';
import MeetingBlock from '@/components/WebsiteProdPage/ScheduleMeeting/MeetingBlock';
import { CalendlyCardDto, WebsiteAuditFormDto } from '@/components/WebsiteProdPage/ScheduleMeeting/types';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
interface Props {
  calendlyCardData?: CalendlyCardDto;
  websiteAuditFormData?: WebsiteAuditFormDto;
  variant: 'calendly-first' | 'calendly-second' | 'website-form';
  isIframe?: boolean;
  className?: string;
  contentClassName?: string;
}
const ScheduleMeeting: FC<Props> = t0 => {
  const $ = _c(30);
  const {
    calendlyCardData,
    websiteAuditFormData,
    variant,
    isIframe,
    className,
    contentClassName
  } = t0;
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const variation = calendlyCardData?.variant ?? variant;
  const shouldLoadVideo = useLazyVideoSource(sectionRef);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [sectionRef];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useViewportVideosPlayback(t1);
  let t2;
  if ($[1] !== isIframe) {
    t2 = () => {
      if (isIframe) {
        return;
      }
      const mm = gsap.matchMedia();
      mm.add({
        isDesktop: "(min-width: 992px)",
        isMobile: "(max-width: 991px)"
      }, context => {
        const {
          isMobile
        } = context.conditions as {
          isMobile: boolean;
        };
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: isMobile ? "60% 90%" : "60% 75%",
            toggleActions: "play none none reverse"
          }
        });
        tl.fromTo(contentRef.current?.children ?? [], {
          y: "50vh",
          ...(isMobile ? {} : {
            rotationX: 90,
            transformPerspective: 1000
          })
        }, {
          y: "0%",
          ...(isMobile ? {} : {
            rotationX: 0
          }),
          duration: 1.4,
          ease: "power3.out",
          stagger: 0.2
        }, "-=0.2");
        tl.fromTo(contentRef.current?.children ?? [], {
          opacity: 0
        }, {
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.2
        }, "<");
      });
    };
    $[1] = isIframe;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isIframe) {
    t3 = {
      scope: sectionRef,
      dependencies: [isIframe]
    };
    $[3] = isIframe;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useGSAP(t2, t3);
  let t4;
  if ($[5] !== calendlyCardData || $[6] !== variation || $[7] !== websiteAuditFormData) {
    t4 = () => {
      switch (variation) {
        case "calendly-first":
        case "calendly-second":
          {
            return calendlyCardData ? <MeetingBlock data={calendlyCardData} className={css.contentBlock} variant={variation} /> : null;
          }
        case "website-form":
          {
            return websiteAuditFormData ? <FormBlock data={websiteAuditFormData} className={css.contentBlock} /> : null;
          }
      }
    };
    $[5] = calendlyCardData;
    $[6] = variation;
    $[7] = websiteAuditFormData;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const renderContent = t4;
  const t5 = variation === "calendly-first" && css.isCalendlyFirst;
  const t6 = variation === "calendly-second" && css.isCalendlySecond;
  let t7;
  if ($[9] !== className || $[10] !== t5 || $[11] !== t6) {
    t7 = clsx(css.section, "container", className, t5, t6);
    $[9] = className;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== shouldLoadVideo || $[14] !== variation) {
    t8 = variation === "calendly-first" ? <><AnimatedBackground staticBlobsPosition="sides-center" /><video loop={true} muted={true} playsInline={true} className={css.video} preload="none">{shouldLoadVideo ? <><source src="/assets/video/mov/qream-pillow-letters.mov" type={"video/quicktime; codecs=\"hvc1\""} /><source src="/assets/video/webm/qream-pillow-letters.webm" type="video/webm" /></> : null}</video></> : null;
    $[13] = shouldLoadVideo;
    $[14] = variation;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== variation) {
    t9 = variation === "calendly-second" ? <Image fill={true} className={css.bgTexture} src="/assets/images/website-prod-page/schedule-meeting/texture.webp" alt="" /> : null;
    $[16] = variation;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== contentClassName) {
    t10 = clsx(css.content, contentClassName);
    $[18] = contentClassName;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== renderContent) {
    t11 = renderContent();
    $[20] = renderContent;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div ref={contentRef} className={t10}>{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t13 = <section ref={sectionRef} className={t7}>{t8}{t9}{t12}</section>;
    $[25] = t12;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
export default ScheduleMeeting;